/**
 * Resolve post-login redirect — same-origin relative paths only.
 */
export function safeNextPath(raw: string | null | undefined, fallback = '/dashboard'): string {
  if (!raw || typeof raw !== 'string') return fallback
  const path = raw.trim()
  if (!path.startsWith('/')) return fallback
  if (path.startsWith('//')) return fallback
  if (/^https?:\/\//i.test(path)) return fallback
  return path
}

/** Minimal role shape for post-auth routing (login API + AuthContext). */
export type PortalRoleUser = {
  user_role?: string | null
  portal_session?: 'vendor' | null
  needs_portal_choice?: boolean
} | null

const PORTAL_PREF_KEY = 'ci_portal_pref'

export function getPortalPref(): 'contractor' | 'vendor' | null {
  try {
    const v = localStorage.getItem(PORTAL_PREF_KEY)
    if (v === 'vendor' || v === 'contractor') return v
  } catch { /* private browsing */ }
  return null
}

export function setPortalPref(v: 'contractor' | 'vendor') {
  try {
    localStorage.setItem(PORTAL_PREF_KEY, v)
  } catch { /* private browsing */ }
}

/**
 * Post-auth landing.
 * Genuine dual identity → chooser. Contractor projects (incl. false dual) → dashboard.
 * Vendor session with real membership → vendor portal.
 */
export function postAuthHome(
  user: PortalRoleUser & { redirect_to?: string | null },
  next?: string | null,
  redirectTo?: string | null,
): string {
  if (user?.portal_session === 'vendor') return '/vendor/dashboard'
  if (user?.needs_portal_choice) return '/auth/portal-choice'
  // Explicit ?next= from login deep links beats server default redirect_to.
  if (next != null && String(next).trim()) {
    return safeNextPath(next)
  }
  const serverRedirect = redirectTo ?? user?.redirect_to
  if (serverRedirect && serverRedirect.startsWith('/') && !serverRedirect.startsWith('//')) {
    return serverRedirect
  }
  return safeNextPath(next)
}
